import React from "react";
import { Grid } from "@mui/material";
import LabeledText from "../../../Widgets/LabeledText";
import { TherapyHours } from "./interface";
import { ClientInsurancePlanCptCodes } from "hooks/api/insurance/useGetClientInsurancePlanCptCodes";

interface ViewFieldsProps {
  therapyHours: TherapyHours;
  cptCodes: ClientInsurancePlanCptCodes | null;
}

// Round-trip through toFixed(2) to avoid floating-point artifacts (e.g. 1.35 - 0.1 = 1.2499...9),
// then strip trailing zeros so whole/half hours still read as "2 hours" / "2.5 hours".
const formatHours = (value: number | null | undefined): string =>
  value === null || value === undefined ? "-" : `${parseFloat(value.toFixed(2))} hours`;

const codeLabel = (name: string, info: { code: string } | null): string =>
  info ? `${name} - ${info.code}` : name;

export const ViewFields: React.FC<ViewFieldsProps> = ({ therapyHours, cptCodes }) => {
  // When cptCodes is provided, only show therapy types that have a mapped code.
  // When cptCodes is null (no insurance plan / not yet loaded), show all rows with hardcoded labels.
  const showAll = cptCodes === null;

  return (
    <Grid container sx={{ rowGap: "24px" }}>
      {(showAll || cptCodes?.assessment) && (
        <LabeledText
          label={codeLabel("Assessment", cptCodes?.assessment ?? null)}
          value={formatHours(therapyHours?.assessment_hours)}
        />
      )}
      {(showAll || cptCodes?.direct_therapy) && (
        <LabeledText
          label={codeLabel("Direct Therapy", cptCodes?.direct_therapy ?? null)}
          value={formatHours(therapyHours?.direct_therapy_hours)}
        />
      )}
      {(showAll || cptCodes?.caregiver_mediated) && (
        <LabeledText
          label={codeLabel("Caregiver Mediated", cptCodes?.caregiver_mediated ?? null)}
          value={formatHours(therapyHours?.caregiver_mediated_hours)}
        />
      )}
      {(showAll || cptCodes?.protocol_modification_direct) && (
        <LabeledText
          label={codeLabel(
            "Direct Protocol Modification",
            cptCodes?.protocol_modification_direct ?? null
          )}
          value={formatHours(therapyHours?.protocol_modification_hours)}
        />
      )}
      {(showAll || cptCodes?.protocol_modification_indirect) && (
        <LabeledText
          label={codeLabel(
            "Indirect Protocol Modification",
            cptCodes?.protocol_modification_indirect ?? null
          )}
          value={formatHours(therapyHours?.indirect_protocol_modification_hours)}
        />
      )}
      {(showAll || cptCodes?.caregiver_training) && (
        <LabeledText
          label={codeLabel("Caregiver Training", cptCodes?.caregiver_training ?? null)}
          value={formatHours(therapyHours?.caregiver_training_hours)}
        />
      )}
    </Grid>
  );
};

export default ViewFields;
